import { fetchCompany } from '@/api/companies';
import * as companies from '@/api/companies';
import * as trades from '@/api/trades';
import * as zips from '@/api/zips';
import * as prioritiesApi from '@/api/priorities';
import * as capacitiesApi from '@/api/capacities';

export async function fetch({ commit, state }, force) {
   if (!force && state.companies.length) {
      return Promise.resolve();
   }
   commit('setFetching', true);
   return companies.indexCompanies()
      .then(companies => {
         commit('setCompanies', companies);
         commit('setFetching', false);
         commit('setLoaded', true);
      });
}

export async function refreshCompany({ commit }, company) {
   return fetchCompany(company)
      .then(refreshed => {
         commit('replaceCompany', refreshed);
      });
}

export async function update({ commit }, payload) {
   const updatingState = {
      slug: payload.slug,
      updating: true,
   };
   commit('setUpdatingCompany', updatingState);
   return companies.updateCompany(payload.slug, payload.update)
      .then(updated => {
         updatingState.updating = false;
         commit('setUpdatingCompany', updatingState);
         commit('replaceCompany', updated);
      });
}

// Capacities
export async function reorderCompanyCapacities({ commit, dispatch }, { company, capacities }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingCapacities', updating);
   return capacitiesApi.reorderCapacities(company, capacities)
      .then(capacities => {
         commit('replaceCapacities', {
            slug: company.slug,
            capacities: capacities,
         });
         updating.updating = false;
         commit('setUpdatingCapacities', updating);
         dispatch('fetch', true);
      });
}

export async function deleteCapacity({ commit, dispatch }, { company, capacity }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingCapacities', updating);
   return capacitiesApi.deleteCapacity(company, capacity)
      .then(() => {
         updating.updating = false;
         commit('setUpdatingCapacities', updating);
         dispatch('refreshCompany', company);
      });
}

export async function storeCapacity({ commit, dispatch }, { company, form }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingCapacities', updating);
   return capacitiesApi.storeCapacity(company, form)
      .then(() => {
         updating.updating = false;
         commit('setUpdatingCapacities', updating);
         dispatch('refreshCompany', company);
      });
}

export async function updateCapacity({ commit, dispatch }, { company, capacity, form }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingCapacities', updating);
   return capacitiesApi.updateCapacity(company, capacity, form)
      .then(() => {
         updating.updating = false;
         commit('setUpdatingCapacities', updating);
         dispatch('refreshCompany', company);
      });
}

// Priorities
export async function reorderCompanyPriorities({ commit, dispatch }, { company, priorities }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingPriorities', updating);
   return prioritiesApi.reorder(company, priorities)
      .then(priorities => {
         commit('replacePriorities', {
            slug: company.slug,
            priorities: priorities,
         });
         updating.updating = false;
         commit('setUpdatingPriorities', updating);
         dispatch('fetch', true);
      });
}

export async function deletePriority({ commit, dispatch }, { company, priority }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingPriorities', updating);
   return prioritiesApi.deletePriority(company, priority)
      .then(() => {
         updating.updating = false;
         commit('setUpdatingPriorities', updating);
         dispatch('refreshCompany', company);
      });
}

export async function storePriority({ commit, dispatch }, { company, form }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingPriorities', updating);
   return prioritiesApi.storePriority(company, form)
      .then(() => {
         updating.updating = false;
         commit('setUpdatingPriorities', updating);
         dispatch('refreshCompany', company);
      });
}

export async function updatePriority({ commit, dispatch }, { company, priority, form }) {
   const updating = { slug: company.slug, updating: true };
   commit('setUpdatingPriorities', updating);
   return prioritiesApi.updatePriority(company, priority, form)
      .then(() => {
         updating.updating = false;
         commit('setUpdatingPriorities', updating);
         dispatch('refreshCompany', company);
      });
}

// Trades
export async function addTradeToBlacklist({ commit }, { company, trade }) {
   return trades.addToBlacklist(company.slug, trade.code)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_trades: updatedBlacklist,
         });
      });
}

export async function removeTradeFromBlacklist({ commit }, { company, trade }) {
   return trades.removeFromBlacklist(company.slug, trade.code)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_trades: updatedBlacklist,
         });
      });
}

export async function setBlacklistedTradeCodes({ commit }, { company, blacklist }) {
   return trades.setBlacklist(company.slug, blacklist)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_trades: updatedBlacklist,
         });
      });
}

export async function setWhitelistedTradeCodes({ commit }, { company, whitelist }) {
   return trades.setWhitelist(company.slug, whitelist)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_trades: updatedBlacklist,
         });
      });
}

// Zips
export async function addZipToBlacklist({ commit }, { company, zip }) {
   return zips.addToBlacklist(company.slug, zip.zip)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_zips: updatedBlacklist,
         });
      });
}

export async function removeZipFromBlacklist({ commit }, { company, zip }) {
   return zips.removeFromBlacklist(company.slug, zip.zip)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_zips: updatedBlacklist,
         });
      });
}

export async function setBlacklistedZipCodes({ commit }, { company, blacklist }) {
   return zips.setBlacklist(company.slug, blacklist)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_zips: updatedBlacklist,
         });
      });
}

export async function setWhitelistedZipCodes({ commit }, { company, whitelist }) {
   return zips.setWhitelist(company.slug, whitelist)
      .then(updatedBlacklist => {
         commit('replaceCompany', {
            ...company,
            blacklisted_zips: updatedBlacklist,
         });
      });
}

// Contacts
export async function createContact({ commit, getters }, { company: { slug }, params }) {
   return companies.createContact(slug, params)
      .then(contact => {
         const company = getters.findBySlug(slug);
         const updatedCompany = {
            ...company,
            contacts: [...company.contacts, contact],
         };
         commit('replaceCompany', updatedCompany);
      });
}

export async function updateContact({ commit, getters }, { company: { slug }, contact: { id }, params }) {
   return companies.updateContact(slug, id, params)
      .then(updatedContact => {
         const company = getters.findBySlug(slug);
         const contacts = company.contacts.slice();
         const idx = contacts.findIndex(c => c.id === id);
         if (idx >= 0) {
            contacts.splice(idx, 1, updatedContact);
            const updatedCompany = {
               ...company,
               contacts,
            };
            commit('replaceCompany', updatedCompany);
         }
      });
}

export async function deleteContact({ commit, getters }, { company: { slug }, contact: { id } }) {
   return companies.deleteContact(slug, id)
      .then(() => {
         const company = getters.findBySlug(slug);
         const contacts = company.contacts.slice();
         const idx = contacts.findIndex(c => c.id === id);
         if (idx >= 0) {
            contacts.splice(idx, 1);
            const updatedCompany = {
               ...company,
               contacts,
            };
            commit('replaceCompany', updatedCompany);
         }
      });
}
